import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { NARRATION } from "@/data/plates";
import { chime } from "@/lib/ambience";
import {
  estimateMs,
  narrationSupported,
  pauseSpeaking,
  resumeSpeaking,
  speak,
  stopSpeaking,
} from "@/lib/narrator";

const STOP_MS = 11000;
const EASE = [0.22, 1, 0.36, 1];
const VOICE_KEY = "aetheria-voice";

export default function TourOverlay({ stops, onStop, onExit, onOpenDossier, suspended }) {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [voice, setVoice] = useState(
    () => narrationSupported() && localStorage.getItem(VOICE_KEY) === "1"
  );
  const startRef = useRef(0);
  const elapsedRef = useRef(0);
  const site = stops[i];
  const last = i === stops.length - 1;
  const text = NARRATION[site.id];
  const duration = voice ? Math.max(STOP_MS, estimateMs(text)) : STOP_MS;

  /* fly + chime on each stop */
  useEffect(() => {
    onStop(site);
    chime();
    elapsedRef.current = 0;
    setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  /* spoken narration */
  useEffect(() => {
    if (voice && !suspended) speak(text);
    else stopSpeaking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i, voice]);
  useEffect(() => () => stopSpeaking(), []);
  useEffect(() => {
    if (!voice) return;
    if (paused || suspended) pauseSpeaking();
    else resumeSpeaking();
  }, [paused, suspended, voice]);

  /* auto-advance clock */
  useEffect(() => {
    if (paused || suspended) return;
    startRef.current = performance.now() - elapsedRef.current;
    let raf;
    const tick = (now) => {
      elapsedRef.current = now - startRef.current;
      const p = Math.min(1, elapsedRef.current / duration);
      setProgress(p);
      if (p >= 1) {
        if (last) {
          setPaused(true);
          return;
        }
        setI((v) => v + 1);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, suspended, i, last, duration]);

  useEffect(() => {
    if (suspended) return;
    const onKey = (e) => {
      if (e.key === "Escape") onExit();
      if (e.key === " ") {
        e.preventDefault();
        setPaused((p) => !p);
      }
      if (e.key === "ArrowRight" && !last) setI((v) => v + 1);
      if (e.key === "ArrowLeft" && i > 0) setI((v) => v - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onExit, last, i, suspended]);

  const toggleVoice = () => {
    const next = !voice;
    localStorage.setItem(VOICE_KEY, next ? "1" : "0");
    setVoice(next);
  };

  const copyTourLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#route=${stops
      .map((s) => s.id)
      .join(",")}&tour=1`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Tour link copied — it opens straight into the narration");
    } catch {
      toast.error("Couldn't copy automatically — grab the URL bar instead");
    }
  };

  return (
    <div data-testid="tour-overlay" className="pointer-events-none fixed inset-0 z-[55]">
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0B0C10] to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-[55vh] bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/70 to-transparent" />

      {/* stop indicator rail */}
      <div className="pointer-events-auto absolute left-5 top-24 hidden flex-col gap-3 lg:left-10 lg:flex">
        {stops.map((s, k) => (
          <button
            key={s.id}
            data-testid={`tour-rail-${s.id}`}
            onClick={() => setI(k)}
            className="group flex items-center gap-3 text-left"
          >
            <span
              className={`h-px transition-all duration-500 ${
                k === i ? "w-10 bg-[#FF9F1C]" : k < i ? "w-6 bg-[#D4AF37]/60" : "w-4 bg-white/20"
              }`}
            />
            <span
              className={`font-mono text-[9px] tracking-[0.25em] transition-colors ${
                k === i ? "text-[#FF9F1C]" : "text-white/35 group-hover:text-white/70"
              }`}
            >
              {String(k + 1).padStart(2, "0")}
            </span>
          </button>
        ))}
      </div>

      {/* narration card */}
      <div className="absolute inset-x-0 bottom-0 flex justify-center px-5 pb-6 sm:pb-10">
        <div className="pointer-events-auto w-full max-w-[720px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={site.id}
              data-testid="tour-card"
              initial={{ opacity: 0, y: 30, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -20, filter: "blur(6px)" }}
              transition={{ duration: 0.6, ease: EASE }}
              className="border border-[#D4AF37]/30 bg-[#12141C]/85 p-6 backdrop-blur-md sm:p-8"
            >
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-mono text-[9px] tracking-[0.35em] text-[#FF9F1C]">
                  STOP {String(i + 1).padStart(2, "0")} / {String(stops.length).padStart(2, "0")}
                  <span className="ml-3 text-[#38BDF8]/80">{site.location.toUpperCase()}</span>
                </p>
                <div className="flex items-center gap-4">
                  <p className="hidden font-mono text-[9px] tracking-[0.2em] text-white/35 sm:block">
                    {site.lat.toFixed(2)}° · {site.lng.toFixed(2)}°
                  </p>
                  {narrationSupported() && (
                    <button
                      data-testid="tour-voice"
                      onClick={toggleVoice}
                      aria-pressed={voice}
                      className={`flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[8px] tracking-[0.25em] transition-colors ${
                        voice
                          ? "border-[#38BDF8]/60 text-[#38BDF8]"
                          : "border-white/20 text-white/45 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          voice ? "animate-pulse bg-[#38BDF8]" : "bg-white/30"
                        }`}
                      />
                      {voice ? "VOICE ON" : "NARRATE"}
                    </button>
                  )}
                </div>
              </div>
              <h3
                data-testid="tour-card-title"
                className="mt-3 font-serif text-2xl leading-tight sm:text-4xl"
              >
                {site.name}
              </h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-white/75 sm:text-base">
                {text}
              </p>
              <div className="mt-5 h-px w-full bg-white/10">
                <div
                  data-testid="tour-progress"
                  className="h-px bg-[#FF9F1C]"
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-2">
                <button
                  data-testid="tour-prev"
                  onClick={() => setI((v) => v - 1)}
                  disabled={i === 0}
                  className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-30"
                >
                  ← PREV
                </button>
                <button
                  data-testid="tour-toggle"
                  onClick={() => {
                    if (last && progress >= 1) {
                      setI(0);
                      setPaused(false);
                    } else setPaused((p) => !p);
                  }}
                  className="border border-[#D4AF37] bg-[#D4AF37]/15 px-5 py-2.5 font-mono text-[9px] tracking-[0.25em] text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0B0C10]"
                >
                  {last && progress >= 1 ? "↻ REPLAY" : paused ? "▶ RESUME" : "❚❚ PAUSE"}
                </button>
                <button
                  data-testid="tour-next"
                  onClick={() => setI((v) => v + 1)}
                  disabled={last}
                  className="border border-white/20 px-4 py-2.5 font-mono text-[9px] tracking-[0.25em] text-white/60 transition-colors hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:cursor-not-allowed disabled:opacity-30"
                >
                  NEXT →
                </button>
                <button
                  data-testid="tour-dossier"
                  onClick={() => onOpenDossier(site)}
                  className="ml-auto font-mono text-[9px] tracking-[0.25em] text-[#38BDF8]/80 transition-colors hover:text-[#38BDF8]"
                >
                  OPEN DOSSIER ↗
                </button>
                <button
                  data-testid="tour-copy-link"
                  onClick={copyTourLink}
                  className="font-mono text-[9px] tracking-[0.25em] text-[#D4AF37]/80 transition-colors hover:text-[#D4AF37]"
                >
                  COPY TOUR LINK
                </button>
                <button
                  data-testid="tour-exit"
                  onClick={onExit}
                  className="font-mono text-[9px] tracking-[0.25em] text-white/40 transition-colors hover:text-[#FF9F1C]"
                >
                  EXIT TOUR
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
          <p className="mt-3 text-center font-mono text-[8px] tracking-[0.3em] text-white/25">
            SPACE · PAUSE &nbsp;&nbsp; ← → · STOPS &nbsp;&nbsp; ESC · EXIT
          </p>
        </div>
      </div>
    </div>
  );
}
